import { useState, useRef } from 'react';
import { PhotoIcon, ArrowUpTrayIcon, PencilIcon } from '@heroicons/react/24/outline';
import { useNotifications } from '../../lib/NotificationContext.tsx';

interface ImageUploadProps {
    currentImageUrl?: string | null;
    onUpload: (file: File) => Promise<void>;
    maxSize?: number;
    allowedTypes?: string[];
    allowedExtensions?: string[];
    uploadButtonText?: string;
    changeButtonText?: string;
    imageAlt?: string;
    sizeHint?: string;
    className?: string;
}

export function ImageUpload({
    currentImageUrl,
    onUpload,
    maxSize = 5 * 1024 * 1024,
    allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/gif', 'image/webp'],
    allowedExtensions = ['.png', '.jpg', '.jpeg', '.svg', '.gif', '.webp'],
    uploadButtonText = 'Afbeelding uploaden',
    changeButtonText = 'Afbeelding wijzigen',
    imageAlt = 'Uploaded image',
    sizeHint,
    className = '',
}: ImageUploadProps) {
    const {dispatch} = useNotifications();
    const [isUploading, setIsUploading] = useState(false);
    const [imageError, setImageError] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(0);
    const defaultSizeHint = sizeHint || `PNG, JPG, SVG - Max ${maxSizeMB} MB`;

    async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        if (!file) return;

        setImageError(false);

        if (!allowedTypes.includes(file.type)) {
            const errorMsg = `Ongeldig bestandstype. Toegestane typen: ${allowedExtensions.join(', ')}`;
            dispatch({
                title: 'Ongeldig bestandstype',
                description: errorMsg,
                type: 'error',
            });
            e.target.value = '';
            return;
        }

        if (file.size > maxSize) {
            const errorMsg = `Bestand is te groot. Maximum grootte: ${maxSizeMB}MB`;
            dispatch({
                title: 'Bestand te groot',
                description: errorMsg,
                type: 'error',
            });
            e.target.value = '';
            return;
        }

        setIsUploading(true);
        try {
            await onUpload(file);
            dispatch({
                title: 'Afbeelding geüpload',
                description: 'De afbeelding is succesvol geüpload.',
                type: 'success',
            });
        } catch (error: any) {
            dispatch({
                title: 'Upload mislukt',
                description: 'Er is een fout opgetreden bij het uploaden van de afbeelding. Probeer het opnieuw.',
                type: 'error',
            });
        } finally {
            setIsUploading(false);
            e.target.value = '';
        }
    }


    if (currentImageUrl) {
        return (
            <>
                <div className={`w-full mb-3 ${className}`}>
                    <div className="flex items-start justify-between mb-4">
                        <div>
                            <div className="mt-2 mb-3">
                                <p className="text-xs text-gray-500">{defaultSizeHint}</p>
                            </div>
                            <input
                                type="file"
                                accept={allowedTypes.join(',')}
                                className="hidden"
                                ref={fileInputRef}
                                disabled={isUploading}
                                onChange={handleFileChange}
                            />
                            <label className="cursor-pointer">
                                <button
                                    type="button"
                                    disabled={isUploading}
                                    onClick={() => {
                                        fileInputRef.current?.click();
                                    }}
                                    className={`h-[34px] rounded-md px-2.5 text-sm font-semibold ring-1 ring-inset ring-gray-300 flex items-center gap-1.5 ${
                                        isUploading
                                            ? 'bg-gray-50 text-gray-700 shadow-sm cursor-not-allowed'
                                            : 'bg-white text-gray-900 shadow-sm hover:bg-gray-50'
                                    }`}
                                >
                                    {!isUploading && <PencilIcon className="h-5 w-5" />}
                                    <span>{isUploading ? 'Uploaden...' : changeButtonText}</span>
                                </button>
                            </label>
                           
                        </div>
                        <div className="h-20 w-20 flex items-center justify-center bg-gray-200 rounded-lg border border-gray-300">
                            {imageError ? (
                                <PhotoIcon className="h-16 w-16 text-gray-400" />
                            ) : (
                                <img
                                    src={currentImageUrl}
                                    alt={imageAlt}
                                    className="h-20 w-20 object-contain rounded-lg"
                                    onError={() => {
                                        setImageError(true);
                                    }}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <div className={`w-full mb-3 ${className}`}>
                <div className="flex flex-col items-center">
                    <div className="mt-2 mb-3">
                        <p className="text-xs text-gray-500">
                            {defaultSizeHint}
                        </p>
                    </div>
                <input
                    type="file"
                    accept={allowedTypes.join(',')}
                    className="hidden"
                    ref={fileInputRef}
                    disabled={isUploading}
                    onChange={handleFileChange}
                />
                <label className="cursor-pointer">
                    <button
                        type="button"
                        disabled={isUploading}
                        onClick={() => {
                            fileInputRef.current?.click();
                        }}
                        className={`h-[34px] rounded-md px-2.5 text-sm font-semibold ring-1 ring-inset ring-gray-300 flex items-center gap-1.5 ${
                            isUploading
                                ? 'bg-gray-50 text-gray-700 shadow-sm cursor-not-allowed'
                                : 'bg-white text-gray-900 shadow-sm hover:bg-gray-50'
                        }`}
                    >
                        {!isUploading && <ArrowUpTrayIcon className="h-5 w-5" />}
                        <span>{isUploading ? 'Uploaden...' : uploadButtonText}</span>
                    </button>
                </label>
                </div>
            </div>
        </>
    );
}

